/**
 * Write a new value into an Automerge document, changing only what differs
 * (RFC 0346).
 *
 * WHY. Assigning a whole object, `d.data = next`, makes Automerge build a new
 * map, list and text for everything inside it, and a string is text, so every
 * string is written again one character at a time. A deck with 40 KB of state
 * reached 2.38 million ops this way: 103 saves wrote about 30,000 ops each,
 * one slide edited or none. Every copy of the document loads all of that
 * history: the daemon, the window's worker and its tab.
 *
 * HOW. Walk the new value against the stored one. Equal values are left alone.
 * Maps and lists are walked into, key by key and index by index. A changed
 * string is written as the edit between the two (`updateText`), not as new
 * text. Anything else that differs is assigned. The result reads exactly as
 * `next` would; only the history is smaller.
 */

import { Automerge as A } from '@automerge/automerge-repo/slim'

type Path = (string | number)[]

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

/** Set `parent[key]` to `next` inside `doc`, writing only what changed. */
export function writeChanged(
  doc: unknown,
  path: Path,
  parent: Record<string | number, unknown>,
  key: string | number,
  next: unknown,
): void {
  const cur = parent[key]
  if (next === undefined) {
    if (key in parent) delete parent[key]
    return
  }
  if (typeof next === 'string') {
    if (typeof cur === 'string') {
      if (cur !== next)
        A.updateText(doc as A.Doc<unknown>, [...path, key], next)
      return
    }
    parent[key] = next
    return
  }
  if (Array.isArray(next)) {
    if (!Array.isArray(cur)) {
      parent[key] = next
      return
    }
    const list = cur as unknown[]
    for (let i = 0; i < next.length; i++) {
      if (i < list.length) {
        writeChanged(doc, [...path, key], list as never, i, next[i])
      } else {
        list.push(next[i])
      }
    }
    if (list.length > next.length) list.splice(next.length)
    return
  }
  if (isPlainObject(next)) {
    if (!isPlainObject(cur)) {
      parent[key] = next
      return
    }
    for (const k of Object.keys(cur)) {
      if (!(k in next)) delete cur[k]
    }
    for (const [k, v] of Object.entries(next)) {
      writeChanged(doc, [...path, key], cur, k, v)
    }
    return
  }
  if (cur !== next) parent[key] = next
}
